import { useId, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { addLanguagesLabel, callerLanguageName, findCallerLanguages } from "@/constants/callerLanguages";
import { deepgramBlock } from "@/constants/transcribers";

interface CallerLanguagePickerProps {
  /** The languages already listed, in their order. */
  listed: string[];
  /** Deepgram hears the caller: a language it cannot hear, or not with the ones listed and ticked, cannot be ticked. */
  deepgram: boolean;
  /** The ticked languages, in the order they were ticked. */
  onAdd: (codes: string[]) => void;
}

/**
 * The Add languages button and its dialog (owner, 2026-11-02): all the languages with a search, several ticked at
 * once and added after the ones listed, in the order ticked. A listed language shows ticked and fixed; while Deepgram
 * hears the caller, one it cannot add is greyed out with why. Cancel, Escape or closing adds nothing, and focus goes
 * back to the button.
 */
export function CallerLanguagePicker({ listed, deepgram, onAdd }: CallerLanguagePickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Plus />
          Add languages
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-4 overflow-y-auto rounded-lg bg-card p-4 sm:max-h-[min(85dvh,44rem)] sm:p-6">
        <LanguageList
          listed={listed}
          deepgram={deepgram}
          onAdd={(codes) => {
            onAdd(codes);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

interface LanguageListProps {
  listed: string[];
  deepgram: boolean;
  onAdd: (codes: string[]) => void;
  onCancel: () => void;
}

/** The dialog's body: it mounts on each open, so the search and the ticks start afresh. */
function LanguageList({ listed, deepgram, onAdd, onCancel }: LanguageListProps) {
  const ids = useId();
  const searchBox = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [ticked, setTicked] = useState<string[]>([]);
  const languages = findCallerLanguages(search);
  // What Deepgram would hear once these are added: a language that does not fit with them cannot be ticked.
  const after = [...listed, ...ticked];

  const toggle = (code: string) =>
    setTicked((now) => (now.includes(code) ? now.filter((other) => other !== code) : [...now, code]));
  // The button goes as the languages come back: focus goes to the search instead of being lost.
  const clearSearch = () => {
    setSearch("");
    searchBox.current!.focus();
  };

  return (
    <>
      <DialogHeader className="pr-8 text-left">
        <DialogTitle>Add caller languages</DialogTitle>
        <DialogDescription>
          Tick the languages your callers speak. They go after the ones already listed, in the order you tick them.
        </DialogDescription>
      </DialogHeader>

      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          ref={searchBox}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or code"
          aria-label="Search languages"
          autoComplete="off"
          className="pl-9"
        />
      </div>

      <p role="status" className="sr-only">
        {languages.length === 1 ? "1 language" : `${languages.length} languages`}
      </p>

      <div className="-mx-4 min-h-40 flex-1 overflow-y-auto border-y px-4 py-3 sm:-mx-6 sm:px-6">
        {languages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-muted-foreground">No language matches “{search.trim()}”.</p>
            <Button type="button" variant="outline" size="sm" onClick={clearSearch}>
              Clear search
            </Button>
          </div>
        ) : (
          <ul aria-label="Languages" className="grid gap-1 sm:grid-cols-2">
            {languages.map(({ code, name }) => {
              const onList = listed.includes(code);
              const isTicked = ticked.includes(code);
              const blocked = deepgram && !onList && !isTicked ? deepgramBlock(after, code) : null;
              const note = onList ? "Already on your list" : blocked;
              const fixed = onList || blocked !== null;
              return (
                <li key={code}>
                  <label
                    className={cn(
                      "flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors",
                      "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-1",
                      // Beside the shadow ring, a transparent outline: Windows' high-contrast mode drops shadows and paints it.
                      "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-transparent",
                      isTicked ? "border-primary bg-primary/5" : "border-transparent",
                      fixed ? "cursor-not-allowed" : "cursor-pointer hover:bg-accent/50",
                    )}
                  >
                    <Checkbox
                      checked={onList || isTicked}
                      disabled={fixed}
                      onCheckedChange={() => toggle(code)}
                      aria-labelledby={`${ids}${code}`}
                      aria-describedby={note ? `${ids}${code}-note` : undefined}
                      className="mt-0.5 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <span className="min-w-0 flex-1">
                      <span id={`${ids}${code}`} className={cn("block text-sm", fixed && "text-muted-foreground")}>
                        {name} <span className="text-xs text-foreground/70">{code}</span>
                      </span>
                      {note && (
                        <span id={`${ids}${code}-note`} className="block text-xs text-foreground/70">
                          {note}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          {ticked.length === 0 ? "None ticked yet" : ticked.map(callerLanguageName).join(", ")}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1 sm:flex-none"
            disabled={ticked.length === 0}
            onClick={() => onAdd(ticked)}
          >
            {addLanguagesLabel(ticked.length)}
          </Button>
        </div>
      </div>
    </>
  );
}
